import { debounce } from 'throttle-debounce';
import { BREAKPOINTS, SEARCH, SETTINGS, PREDICTIVE_SEARCH } from '../../utils/constants';
import getLocalePrefix from '../../lib/getLocalePrefix';
import reinitOkendoWidgets from '../../utils/reinitOkendoWidgets';
import initProductCardVariants from '../../components/product-card-variants';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/bodyScrollLock';

const initSearch = () => {
  const predictiveSearchEls = document.querySelectorAll('[data-predictive-search]');
  if (!predictiveSearchEls.length) return;

  let clearSearch = null;

  predictiveSearchEls.forEach(search => {
    const searchInputEl = search.querySelector('input[type="search"]');
    const searchInputInternalEl = search.querySelector('[data-search-internal-input]');
    const resultsEl = search.querySelector('[data-predictive-search-results]');
    const resultsContentEl = search.querySelector('[data-search-results-content]');
    if (!searchInputEl || !resultsEl || !resultsContentEl) return;

    const searchDefaultContentEl = search.querySelector('[data-search-default-content]');
    const searchCloseEl = search.querySelector('[data-predictive-search-close]');
    const settings = JSON.parse(search.dataset.settings);
    const blogUrl = search.dataset.predictiveSearchBlogUrl;
    const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
    const isMobile = () => !desktopMediaQuery.matches;
    let isScrollLocked = false;

    // For hoisting
    let handleOutsideClick = null;

    const lockSearchScroll = () => {
      if (isScrollLocked || !isMobile()) return;
      lockBodyScroll(SEARCH.SCROLL_LOCK_TOKEN);
      isScrollLocked = true;
    };

    const unlockSearchScroll = () => {
      if (!isScrollLocked) return;
      unlockBodyScroll(SEARCH.SCROLL_LOCK_TOKEN);
      isScrollLocked = false;
    };

    const openResults = () => {
      resultsEl.classList.remove('hidden');
      lockSearchScroll();
      document.addEventListener('click', handleOutsideClick);
      if (searchInputInternalEl && window.matchMedia('(max-width: 1023px)').matches) {
        requestAnimationFrame(() => searchInputInternalEl.focus());
      }
    };

    const closeResults = () => {
      resultsEl.classList.add('hidden');
      unlockSearchScroll();
      searchDefaultContentEl?.classList.remove('hidden', 'lg:hidden');
      document.removeEventListener('click', handleOutsideClick);
    };

    const handleBreakpointChange = () => {
      if (desktopMediaQuery.matches) unlockSearchScroll();
    };

    clearSearch = () => {
      searchInputEl.value = '';
      if (searchInputInternalEl) searchInputInternalEl.value = '';
      resultsContentEl.innerHTML = '';
      closeResults();
    };

    handleOutsideClick = (e) => {
      if (!search.contains(e.target)) clearSearch();
    };

    const handleCloseClick = () => {
      clearSearch();
      searchInputEl.blur();
    };

    const fetchResults = async (query) => {
      const localePrefix = getLocalePrefix();
      const limit = Math.min(settings?.limit ?? PREDICTIVE_SEARCH.MAX_RESULTS_PER_TYPE, PREDICTIVE_SEARCH.MAX_RESULTS_PER_TYPE);
      const url = `${window.location.origin}${localePrefix}/search/suggest?q=${encodeURIComponent(query)}&resources[type]=query,${settings?.type}&resources[options][fields]=${settings?.fields}&resources[limit]=${limit}&resources[limit_scope]=each&resources[options][unavailable_products]=${settings?.oos}&section_id=predictive-search`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          closeResults();
          throw new Error(`Fetch failed: ${response.status}`);
        }

        const htmlText = await response.text();
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const section = html.querySelector('#shopify-section-predictive-search');

        if (section) {
          searchDefaultContentEl?.classList.add('hidden', 'lg:hidden');
          resultsContentEl.innerHTML = section.innerHTML;
          resultsContentEl.querySelectorAll('[data-predictive-search-blog-link]').forEach((link) => {
            if (blogUrl) link.href = blogUrl;
          });
          initProductCardVariants(resultsContentEl);
          reinitOkendoWidgets();
        } else {
          closeResults();
        }
      } catch (error) {
        closeResults();
        console.error('Search error:', error);
      }
    };

    const handleInput = () => {
      const value = searchInputEl.value.trim();
      if (!value) {
        searchDefaultContentEl?.classList.remove('hidden', 'lg:hidden');
        resultsContentEl.innerHTML = '';
        return;
      }
      fetchResults(value);
    };

    const debouncedHandleInput = debounce(SETTINGS.SEARCH_DEBOUNCE_DELAY, handleInput);

    searchInputEl.addEventListener('input', debouncedHandleInput);
    searchInputEl.addEventListener('click', openResults);
    desktopMediaQuery.addEventListener('change', handleBreakpointChange);

    searchInputInternalEl?.addEventListener('input', () => {
      searchInputEl.value = searchInputInternalEl.value;
      debouncedHandleInput();
    });

    searchCloseEl?.addEventListener('click', handleCloseClick);
  });

  window.theme.search = { initSearch, clearSearch };
};

export default initSearch;
